import { useState } from "react";
import { Check, X } from "lucide-react";
import { useDataProvider, useNotify, useRefresh, useTranslate } from "ra-core";
import { useMutation } from "@tanstack/react-query";
import { Confirm } from "@/components/admin/confirm";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

import {
  getTeamRosterClaimAuthorization,
  type Actor,
} from "../providers/commons/canAccess";
import type { CrmDataProvider } from "../providers/types";
import type { Sale, TeamRosterEntry } from "../types";
import { BUSINESS_ARM_LABELS } from "./businessArms";
import { ROLE_LABELS } from "./roles";

// design-system.md section 8: a designed empty value, never a raw
// "null"/"undefined".
const EMPTY_VALUE = "—";

const claimantLabel = (claimant?: Sale) =>
  claimant ? `${claimant.first_name} ${claimant.last_name}` : EMPTY_VALUE;

export interface RosterClaimRowProps {
  actor: Actor;
  claim: TeamRosterEntry;
  claimant?: Sale;
  accompanimentName?: string;
  projects: Array<{ name: string; isLider: boolean }>;
}

/**
 * One "Claves por confirmar" row (Almikia Epic E3b-2, TASK-085): roster
 * details next to the claimant's, and the Confirmar/Rechazar actions --
 * split out of `PendingRosterClaims.tsx` to keep both files under the
 * coding-style.md ~400-line ceiling.
 */
export const RosterClaimRow = ({
  actor,
  claim,
  claimant,
  accompanimentName,
  projects,
}: RosterClaimRowProps) => {
  const translate = useTranslate();
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider<CrmDataProvider>();
  const [pendingAction, setPendingAction] = useState<
    "confirm" | "reject" | null
  >(null);

  const { canAct, roleChangeRequiresDireccion } =
    getTeamRosterClaimAuthorization(
      actor,
      claim,
      claimant
        ? {
            id: claimant.id,
            role: claimant.role,
            business_arm: claimant.business_arm ?? null,
          }
        : undefined,
    );

  const { mutate: confirm, isPending: isConfirming } = useMutation({
    mutationFn: () => dataProvider.confirmTeamRosterMatch(claim.id),
    onSuccess: () => {
      setPendingAction(null);
      refresh();
      notify("resources.sales.pending_claims.confirm_success", {
        type: "success",
      });
    },
    onError: (error: Error) => {
      setPendingAction(null);
      notify(error.message, { type: "error" });
    },
  });

  const { mutate: reject, isPending: isRejecting } = useMutation({
    mutationFn: () => dataProvider.rejectTeamRosterMatch(claim.id),
    onSuccess: () => {
      setPendingAction(null);
      refresh();
      notify("resources.sales.pending_claims.reject_success", {
        type: "success",
      });
    },
    onError: (error: Error) => {
      setPendingAction(null);
      notify(error.message, { type: "error" });
    },
  });

  return (
    <div
      role="group"
      aria-label={claim.full_name}
      className="flex flex-col gap-3 rounded-2xl border border-border/60 p-4"
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-sm font-semibold">{claim.full_name}</p>
          <p className="text-sm text-muted-foreground">{claim.position}</p>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.pending_claims.roster_clave")}
              </dt>
              <dd>{claim.advisor_code || EMPTY_VALUE}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.fields.business_arm")}
              </dt>
              <dd>
                {claim.business_arm
                  ? BUSINESS_ARM_LABELS[claim.business_arm]
                  : EMPTY_VALUE}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.pending_claims.projects")}
              </dt>
              <dd>
                {projects.length
                  ? projects
                      .map((project) =>
                        project.isLider
                          ? translate(
                              "resources.sales.pending_claims.lider_bpo_marker",
                              { name: project.name },
                            )
                          : project.name,
                      )
                      .join(", ")
                  : EMPTY_VALUE}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.fields.reports_to")}
              </dt>
              <dd>{accompanimentName ?? EMPTY_VALUE}</dd>
            </div>
          </dl>
        </div>
        <div>
          <p className="text-sm font-semibold">{claimantLabel(claimant)}</p>
          <p className="text-sm text-muted-foreground">{claimant?.email}</p>
          <dl className="mt-2 space-y-1 text-sm">
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.fields.advisor_code")}
              </dt>
              <dd>{claimant?.advisor_code || EMPTY_VALUE}</dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.fields.business_arm")}
              </dt>
              <dd>
                {claimant?.business_arm
                  ? BUSINESS_ARM_LABELS[claimant.business_arm]
                  : EMPTY_VALUE}
              </dd>
            </div>
            <div className="flex gap-2">
              <dt className="text-muted-foreground">
                {translate("resources.sales.fields.role")}
              </dt>
              <dd>
                <Badge variant="outline">
                  {claimant ? ROLE_LABELS[claimant.role] : EMPTY_VALUE}
                </Badge>
              </dd>
            </div>
          </dl>
        </div>
      </div>

      {roleChangeRequiresDireccion ? (
        <p className="text-sm text-muted-foreground">
          {translate("resources.sales.pending_claims.direccion_only")}
        </p>
      ) : (
        <div className="flex justify-end gap-2">
          <Button
            variant="outline"
            size="sm"
            disabled={!canAct || isConfirming || isRejecting}
            onClick={() => setPendingAction("reject")}
          >
            <X className="h-4 w-4" />
            {translate("resources.sales.pending_claims.reject")}
          </Button>
          <Button
            size="sm"
            disabled={!canAct || isConfirming || isRejecting}
            onClick={() => setPendingAction("confirm")}
          >
            <Check className="h-4 w-4" />
            {translate("resources.sales.pending_claims.confirm")}
          </Button>
        </div>
      )}

      <Confirm
        isOpen={pendingAction === "confirm"}
        loading={isConfirming}
        confirm="resources.sales.pending_claims.confirm"
        title={translate("resources.sales.pending_claims.confirm_title")}
        content={
          <div className="text-sm space-y-2">
            <p>
              {translate("resources.sales.pending_claims.confirm_description", {
                name: claimantLabel(claimant),
              })}
            </p>
            <dl className="space-y-1">
              <div className="flex gap-2">
                <dt className="text-muted-foreground">
                  {translate("resources.sales.fields.advisor_code")}
                </dt>
                <dd>
                  {claimant?.advisor_code || EMPTY_VALUE} →{" "}
                  {claim.advisor_code || claimant?.advisor_code || EMPTY_VALUE}
                </dd>
              </div>
              <div className="flex gap-2">
                <dt className="text-muted-foreground">
                  {translate("resources.sales.fields.business_arm")}
                </dt>
                <dd>
                  {claimant?.business_arm
                    ? BUSINESS_ARM_LABELS[claimant.business_arm]
                    : EMPTY_VALUE}{" "}
                  →{" "}
                  {claim.business_arm
                    ? BUSINESS_ARM_LABELS[claim.business_arm]
                    : claimant?.business_arm
                      ? BUSINESS_ARM_LABELS[claimant.business_arm]
                      : EMPTY_VALUE}
                </dd>
              </div>
              {/* Role only ever changes for direccion -- a lead_manager
                  never reaches this dialog for a role-changing claim, see
                  `roleChangeRequiresDireccion` above. */}
              {claim.role && claim.role !== claimant?.role ? (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">
                    {translate("resources.sales.fields.role")}
                  </dt>
                  <dd>
                    {claimant ? ROLE_LABELS[claimant.role] : EMPTY_VALUE} →{" "}
                    {ROLE_LABELS[claim.role]}
                  </dd>
                </div>
              ) : null}
              {accompanimentName ? (
                <div className="flex gap-2">
                  <dt className="text-muted-foreground">
                    {translate("resources.sales.fields.reports_to")}
                  </dt>
                  <dd>{accompanimentName}</dd>
                </div>
              ) : null}
            </dl>
          </div>
        }
        onConfirm={() => confirm()}
        onClose={() => setPendingAction(null)}
      />
      <Confirm
        isOpen={pendingAction === "reject"}
        loading={isRejecting}
        confirmColor="warning"
        confirm="resources.sales.pending_claims.reject"
        title={translate("resources.sales.pending_claims.reject_title")}
        content={translate(
          "resources.sales.pending_claims.reject_description",
          { name: claimantLabel(claimant) },
        )}
        onConfirm={() => reject()}
        onClose={() => setPendingAction(null)}
      />
    </div>
  );
};
